import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Navigate } from 'react-router-dom';
import { Page, BlockStack, Card, Text, Form, FormLayout, RadioButton, Button, Banner, Checkbox, Divider, DescriptionList, ButtonGroup, InlineStack } from '@shopify/polaris';
import PageFooter from '../partials/PageFooter';

import { updateSettings, getSettings } from '../../redux/actions/settings';
import LoadingPlaceholder from '../partials/LoadingPlaceholder';
import { generateAllBarcodeImages, generateAllBarcodeValues } from '../../redux/actions/barcodes';
import RequiresRefreshBanner from '../partials/RequiresRefreshBanner';

const AppSettings = ({ shopUrl, payload, settings, submitSettings, settingsLoad, settingsHash, generateAllImages, generateAllValues, barcodes, requiresRefresh }) => {
  const [submitted, setSubmitted] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(true);

  const [settingsForm, setSettingsForm] = useState(settingsHash);
  const [allowQuantityUpdates, setAllowQuantityUpdates] = useState(settingsHash['allow_quantity_updates']);

  const [barcodeImageGenerationField, setBarcodeImageGenerationField] = useState(settingsHash['barcode_image_generation_field']);
  const [barcodeImageSymbolType, setBarcodeImageSymbolType] = useState(settingsHash['barcode_image_symbol_type']);

  const [randomBarcodeValues, setRandomBarcodeValues] = useState(false);
  const [skipExistingBarcodeValues, setSkipExistingBarcodeValues] = useState(false);
  const [acceptDangers, setAcceptDangers] = useState(false);
  const [acceptImageDangers, setAcceptImageDangers] = useState(false);

  const [submittedValueGeneration, setSubmittedValueGeneration] = useState(false);
  const [submittedImageGeneration, setSubmittedImageGeneration] = useState(false);

  const [generateAllField, setGenerateAllField] = useState(false);
  const [generateAllType, setGenerateAllType] = useState(false);

  useEffect(() => {
    settingsLoad(shopUrl, payload);
  }, []);

  useEffect(() => {
    setSubmitted(false);
  }, [settings]);

  useEffect(() => {
    setSettingsForm(settingsHash);
    setAllowQuantityUpdates(settingsHash['allow_quantity_updates']);
    setBarcodeImageGenerationField(settingsHash['barcode_image_generation_field']);
    setBarcodeImageSymbolType(settingsHash['barcode_image_symbol_type']);

    if (!generateAllField) { setGenerateAllField(settingsHash['barcode_image_generation_field']) };
    if (!generateAllType) { setGenerateAllType(settingsHash['barcode_image_symbol_type']) };
  }, [settingsHash]);

  const handleImageGenerationField = (newValue, id) => {
    let updatedSettings = settingsForm;
    updatedSettings['barcode_image_generation_field'] = id;

    setBarcodeImageGenerationField(id);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const handleImageSymbolType = (newValue, id) => {
    let updatedSettings = settingsForm;
    updatedSettings['barcode_image_symbol_type'] = id;

    setBarcodeImageSymbolType(id);
    setSettingsForm(updatedSettings);
    setSettingsSaved(false);
  }

  const handleSubmit = () => {
    setSubmitted(true);
    setGenerateAllField(false);
    setGenerateAllType(false);

    submitSettings(shopUrl, payload, settingsForm);
    setSettingsSaved(true);
  }

  const generateBarcodeValues = () => {
    setSubmittedValueGeneration(true);
    generateAllValues(shopUrl, payload, randomBarcodeValues, skipExistingBarcodeValues);
  }

  const generateImages = () => {
    setSubmittedImageGeneration(true);
    generateAllImages(shopUrl, payload);
  }

  if (settings.status === 'success') {
    return(
      <Page 
        title="App Settings and Bulk Operations"
        backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
      >
        { requiresRefresh &&
          <RequiresRefreshBanner />
        }
        <BlockStack gap="500">
          <Card>
            <Text variant="headingXl" as="h4">Barcode Settings</Text>
            <Form onSubmit={handleSubmit}>
              <FormLayout>

                <DescriptionList
                  items={[
                    {
                      term: 'Barcode Image Generation Field',
                      description:
                        <>
                          <RadioButton
                            label="Barcode"
                            helpText="When generating barcode images, the app will use the value found in the Barcode field for variants"
                            id="barcode"
                            name="barcodeImageGenerationField"
                            checked={barcodeImageGenerationField === 'barcode'}
                            onChange={handleImageGenerationField}
                          />
                          <RadioButton
                            label="SKU"
                            helpText="When generating barcode images, the app will use the value found in the SKU field for variants"
                            checked={barcodeImageGenerationField === 'sku'}
                            id="sku"
                            name="barcodeImageGenerationField"
                            onChange={handleImageGenerationField}
                          />
                        </>
                        
                    },
                    {
                      term: 'Barcode Image Type',
                      description:
                        <>
                          <RadioButton
                            label="Code 128"
                            helpText="When generating barcode images, the app will generate Code 128 barcodes"
                            id="code128"
                            name="barcodeImageType"
                            checked={barcodeImageSymbolType === 'code128'}
                            onChange={handleImageSymbolType}
                          />
                          <RadioButton
                            label="UPCA"
                            helpText="When generating barcode images, the app will generate UPCA barcodes, if possible"
                            checked={barcodeImageSymbolType === 'upca'}
                            id="upca"
                            name="barcodeImageType"
                            onChange={handleImageSymbolType}
                          />
                          <RadioButton
                            label="EAN13"
                            helpText="When generating barcode images, the app will generate EAN13 barcodes, if possible"
                            checked={barcodeImageSymbolType === 'ean13'}
                            id="ean13"
                            name="barcodeImageType"
                            onChange={handleImageSymbolType}
                          />
                          <RadioButton
                            label="EAN8"
                            helpText="When generating barcode images, the app will generate EAN8 barcodes, if possible"
                            checked={barcodeImageSymbolType === 'ean8'}
                            id="ean8"
                            name="barcodeImageType"
                            onChange={handleImageSymbolType}
                          />
                        </>
                    }
                ]} />

                <InlineStack align="end">
                  <ButtonGroup>
                    <Button variant="primary" submit>Update Barcode Options</Button>
                  </ButtonGroup>
                </InlineStack>
              </FormLayout>
            </Form>
          </Card>

          <Card>
            <BlockStack gap="500">
              <Text variant="headingXl" as="h4">Generate All Barcodes</Text>
              <Text>Generate barcodes for all variants in your store. By default, the app will generate values based on the Shopify variant ID. If the "Randomize barcode values" option is selected, the app will generate random 12 character long alphanumeric barcode values.</Text>

              { !skipExistingBarcodeValues &&
                <Banner
                  title="Generating barcode values will replace any existing barcode values, and can't be undone. Backing up your product data is recommended before generating barcode values."
                  tone="warning"
                >
                </Banner>
              }

              { skipExistingBarcodeValues &&
                <Banner
                  title="ScanWay will try to skip existing barcodes when generating new barcode values. There are rare cases where ScanWay may incorrectly identify a barcode value as empty. Backing up your product data is recommended before generating barcode values."
                  tone="warning"
                >
                </Banner>
              }

              <Banner
                title="If another app or system relies on existing barcode values, you may need to update those apps and systems after new barcodes have been generated."
                tone="warning"
              >
              </Banner>

              <Checkbox
                label="Randomize barcode values"
                checked={randomBarcodeValues}
                onChange={() => setRandomBarcodeValues(!randomBarcodeValues)}
              />

              <Checkbox
                label="Skip Existing Barcodes"
                checked={skipExistingBarcodeValues}
                onChange={() => setSkipExistingBarcodeValues(!skipExistingBarcodeValues)}
              />

              <Divider />

              <Checkbox
                label="I have read and understand the above warnings about barcode value generation. I accept the risks and wish to proceed with generating new barcode values for all of my store's products."
                checked={acceptDangers}
                onChange={() => setAcceptDangers(!acceptDangers)}
              />

              { submittedValueGeneration && !barcodes.isFetching &&
                <Banner
                  title="Barcode values are generating and should be complete in a few minutes. Check your store's products to see new barcode values."
                  tone="info"
                >
                </Banner>
              }

              <Button 
                disabled={!acceptDangers || submittedValueGeneration}
                loading={submittedValueGeneration && barcodes.isFetching}
                onClick={() => generateBarcodeValues()}
              >
                Generate All Barcodes
              </Button>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="500">
              <Text variant="headingXl" as="h4">Generate All Barcode Images</Text>
              <Text>Generate all barcode images based on the saved type and field settings above.</Text>
              <Text>The barcodes will be generated from the <Text as="span" fontWeight="bold">{generateAllField}</Text> field.</Text>
              <Text>This action will generate <Text as="span" fontWeight="bold">{generateAllType}</Text> barcodes.</Text>
              <Banner
                title="Generating barcode images will replace existing barcode images, and may take a significant amount of time."
                tone="warning"
              >
              </Banner>
              <Banner
                title="For UPC and EAN barcodes, barcode images will not generate for invalid values."
                tone="warning"
              >
              </Banner>
              
              <Divider />

              <Checkbox
                label="I have read and understand the above warnings about barcode image generation. I accept the risks and wish to proceed with generating new barcode images for all of my store's products."
                checked={acceptImageDangers}
                onChange={() => setAcceptImageDangers(!acceptImageDangers)}
              />

              { submittedImageGeneration && !barcodes.isFetching &&
                <Banner
                  title="Barcode images are generating and should be complete in a few minutes. Search for products in the Manage and Print Product Labels tool to see images that have been generated."
                  tone="info"
                >
                </Banner>
              }

              <Button 
                disabled={!acceptImageDangers || submittedImageGeneration}
                loading={submittedImageGeneration && barcodes.isFetching}
                onClick={() => generateImages()}
              >
                Generate All Barcode Images
              </Button>
            </BlockStack>
          </Card>

        </BlockStack>

        <button onClick={() => {throw new Error("Manually capture in Sentry");}}>.</button>
        
        <PageFooter stripeBackground='#ef8f2f' helpLink="inventory-management/app-settings-2/" helpDescription="how each app settings work" />
      </Page>
    )
  }

  return(
    <Page 
      title="App Settings and Bulk Operations"
      backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
    >
      { requiresRefresh &&
        <RequiresRefreshBanner />
      }
      <BlockStack gap="500">
        <LoadingPlaceholder/>
        <LoadingPlaceholder/>
        <LoadingPlaceholder/>
      </BlockStack>

      <PageFooter stripeBackground='#ef8f2f' helpLink="inventory-management/app-settings-2/" helpDescription="how each app settings work" />
    </Page>
  )
}

const mapStateToProps = state => ({
  requiresRefresh: state.activeToken.requiresRefresh,
  stores: state.stores,
  settings: state.settings,
  settingsHash: state.settings.settings,
  barcodes: state.barcodes,
});

const mapDispatchToProps = (dispatch) => ({
  submitSettings: (storeUrl, payload, newSettings) => dispatch(updateSettings(storeUrl, payload, newSettings)),
  settingsLoad: (storeUrl, payload) => dispatch(getSettings(storeUrl, payload)),
  generateAllImages: (shopUrl, payload) => dispatch(generateAllBarcodeImages(shopUrl, payload)),
  generateAllValues: (shopUrl, payload, random, skipExisting) => dispatch(generateAllBarcodeValues(shopUrl, payload, random, skipExisting)),
});


export default connect(mapStateToProps, mapDispatchToProps)(AppSettings);